import {
    BTLocalStorage,
    BTSessionStorage,
    localStorageDefaultValues,
    LocalStorageKeys,
} from "types/btStorage";

import { getGlobalValue } from "utilities/globalValueUtils";
import { formattedPlural } from "utilities/string/string";

import {
    type IApiJobBuilderRequest,
    JobPickerFilterTypes,
    type JobPickerJob,
} from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import {
    type IJobPickerPersistantState,
    type IJobPickerSelectionMemory,
    JobIdTypes,
    JobPickerSelectModes,
} from "commonComponents/utilities/JobPicker/JobPicker.types";
import {
    getJobPickerStorageKey,
    getStatusTextForJobFilter,
} from "commonComponents/utilities/JobPicker/JobPicker.utils";

import { JobStatus } from "entity/job/Job.api.types";

// TODO: highly recommanded that keyword searching be done on the serverside through the API
// API should be adjusted to support this, if it has not already, along with pagination instead of trying to handle this on the client
//
export function filterJobsList(
    jobsList: JobPickerJob[],
    filterText: string,
    templatesOnly: boolean,
    allJobText?: string,
): JobPickerJob[] {
    const filter = filterText.trim().toLocaleLowerCase();
    const filteredJobsList =
        filter === ""
            ? jobsList
            : jobsList.filter(
                  (x) =>
                      x.jobId === JobIdTypes.AllJobs ||
                      x.jobId === JobIdTypes.GlobalDocs ||
                      x.jobName.toLocaleLowerCase().includes(filter) ||
                      x.jobInfo?.ownerName?.toLocaleLowerCase()?.includes(filter),
              );

    // Check for special case with TimeClock with no jobs
    if (
        jobsList.length === 2 &&
        jobsList.filter((x) => !isSpecialJob(x.jobId, x.jobStatus)).length === 0
    ) {
        return jobsList.filter((x) => x.jobId !== JobIdTypes.AllJobs).map((x) => x);
    }

    // Set all listed count after omitting all listed, none, and special jobs
    const allListedJob = filteredJobsList.filter((x) => !isSpecialJob(x.jobId, x.jobStatus));
    const builderId = allListedJob.length > 0 ? allListedJob[0].builderId : undefined;
    setAllListedJobCount(
        allListedJob.length,
        filteredJobsList,
        templatesOnly,
        builderId,
        allJobText,
    );

    return filteredJobsList;
}

function setAllListedJobCount(
    count: number,
    jobsList: JobPickerJob[],
    templatesOnly: boolean,
    builderId?: number,
    allJobText?: string,
) {
    let statusText = "";
    if (builderId !== undefined) {
        try {
            const selectedJobStatuses: string[] = (
                JSON.parse(
                    // eslint-disable-next-line no-restricted-globals
                    localStorage.getItem(
                        getJobPickerStorageKey(
                            LocalStorageKeys.CurrentJobPickerFilter,
                            false,
                            builderId,
                        ),
                    ) ?? "{}",
                )[JobPickerFilterTypes.Other] ?? ""
            ).split(",");
            if (selectedJobStatuses.length === 1 || selectedJobStatuses.length === 2) {
                statusText =
                    selectedJobStatuses
                        .map((id) => getStatusTextForJobFilter(Number(id)))
                        .join(" and ") + " ";
            }
        } catch (e) {
            statusText = "";
        }
    }
    const allListedJob = jobsList.find((x) => x.jobId === JobIdTypes.AllJobs);
    if (allListedJob !== undefined) {
        if (allJobText) {
            allListedJob.jobName = allJobText;
        } else {
            allListedJob.jobName = templatesOnly
                ? formattedPlural({
                      value: count,
                      one: "View 1 Template",
                      other: `All ${count} Listed Templates`,
                  })
                : formattedPlural({
                      value: count,
                      one: `View 1 ${statusText}Job`,
                      other: `All ${count} ${statusText}Jobs`,
                  });
        }
    }
}

export function isSpecialJob(jobId: number, jobStatus: JobStatus) {
    if (jobStatus === JobStatus.GeneralJob) {
        return true;
    }
    return [
        JobIdTypes.AllJobs,
        JobIdTypes.GlobalDocs,
        JobIdTypes.GlobalJob,
        JobIdTypes.NoJobs,
    ].includes(jobId);
}

export function getSelectedJobIdsForJobPicker(
    id: number,
    jobs: JobPickerJob[] | IApiJobBuilderRequest[],
    selectMode?: JobPickerSelectModes,
) {
    if (
        id === JobIdTypes.NoJobs ||
        (id === JobIdTypes.AllJobs && selectMode === JobPickerSelectModes.Single)
    ) {
        return null;
    }
    if (id === JobIdTypes.AllJobs) {
        return jobs
            .filter((x) => x.jobId !== JobIdTypes.AllJobs && x.jobId !== JobIdTypes.NoJobs)
            .map((x) => x.jobId);
    }
    return id;
}

export function getSelectedJobIds(): number[] {
    const jobPickerState = getJobPickerState();
    return jobPickerState.selectedJobIds.map((x) => Number(x));
}

export function getAllBuildersSelected(): boolean {
    const jobPickerState = getJobPickerState();
    return jobPickerState.allBuildersSelected ?? false;
}

function getModeKey(isTemplateMode: boolean): "jobs" | "templates" {
    return isTemplateMode ? "templates" : "jobs";
}

/**
 * Reads the stored jobsite selections, migrating the legacy shape that held a single selection
 * with no mode. Legacy values are treated as a jobs selection since template mode did not
 * persist a selection at the time they were written.
 */
function getSelectedJobsites(): {
    jobs?: IJobPickerSelectionMemory;
    templates?: IJobPickerSelectionMemory;
} {
    const storedValue = BTLocalStorage.get("bt-object-selectedJobsiteIdWithBuilder");
    if (storedValue === null || storedValue === undefined) {
        return {};
    }
    if ("jobId" in storedValue) {
        return { jobs: { jobId: storedValue.jobId, builderId: storedValue.builderId } };
    }
    return storedValue;
}

/**
 * Stores the selected jobsite for the given mode, so switching modes can restore it. Jobs and
 * templates are stored separately and neither overwrites the other.
 * @param jobId The jobsite ID to store
 * @param builderId The builder ID associated with the jobsite
 * @param isTemplateMode Which mode the jobsite was selected in
 */
export function setSelectedJobsiteIdWithBuilder(
    jobId: number,
    builderId: number,
    isTemplateMode: boolean,
): void {
    BTLocalStorage.set("bt-object-selectedJobsiteIdWithBuilder", {
        ...getSelectedJobsites(),
        [getModeKey(isTemplateMode)]: { jobId, builderId },
    });
}

/**
 * Gets the jobsite selected in the given mode, validating that the stored builder ID matches the
 * current builder
 * @param currentBuilderId The current builder ID to validate against
 * @param isTemplateMode Which mode to read the selection for, defaulting to the current mode
 * @returns The stored jobsite ID if the builder ID matches, null otherwise
 */
export function getSelectedJobsiteIdWithBuilder(
    currentBuilderId: number,
    isTemplateMode: boolean = getJobPickerState().isTemplateMode,
): number | null {
    const stored = getSelectedJobsites()[getModeKey(isTemplateMode)];

    if (stored !== undefined && stored.builderId === currentBuilderId) {
        return stored.jobId;
    }
    // No stored value for this mode, or it belongs to a different builder
    return null;
}

/**
 * Forgets the jobsite selected in the given mode, or in every mode when no mode is given. Used
 * when a stored selection is no longer valid, so it is not restored again.
 */
export function clearSelectedJobsiteIdWithBuilder(isTemplateMode?: boolean): void {
    if (isTemplateMode === undefined) {
        BTLocalStorage.remove("bt-object-selectedJobsiteIdWithBuilder");
        return;
    }
    BTLocalStorage.set("bt-object-selectedJobsiteIdWithBuilder", {
        ...getSelectedJobsites(),
        [getModeKey(isTemplateMode)]: undefined,
    });
}

export function getSelectedJobId(builderId: number): number {
    // Use the new helper function that validates builder ID
    const storedJobId = getSelectedJobsiteIdWithBuilder(builderId);
    if (storedJobId !== null && storedJobId !== undefined) {
        return storedJobId;
    }

    const jobPickerState = getJobPickerState();
    const selectedJobIds = jobPickerState.selectedJobIds;
    const isAllJobsSelected =
        jobPickerState.selectedJobIds.length > 1 && jobPickerState.isAllJobsSelected;
    if (!selectedJobIds || selectedJobIds.length === 0) {
        return JobIdTypes.NoJobs;
    }

    if (isAllJobsSelected || selectedJobIds?.length > 1) {
        return JobIdTypes.AllJobs;
    } else if (selectedJobIds?.length === 1) {
        return selectedJobIds[0];
    }
    return JobIdTypes.NoJobs;
}

export function selectJobs(jobIds: number[], allJobsSelected: boolean) {
    const jobPickerState = getJobPickerState();
    const storageValue = {
        ...jobPickerState,
        selectedJobIds: jobIds,
        isAllJobsSelected: allJobsSelected,
    };
    setJobPickerState(storageValue);
}

export function setTemplateMode() {
    const jobPickerState = getJobPickerState();
    const storageValue = {
        ...jobPickerState,
        selectedJobIds: [],
        isAllJobsSelected: false,
        isTemplateMode: true,
    };
    setJobPickerState(storageValue);
}

function getCurrentBuilderId(): number {
    // getGlobalValue is deprecated for SPA components in favor of BuilderInfoContext, but this is a
    // plain utility called from webforms-hosted code and module scope where no React context is in
    // scope. EntityListWrapper reads the builder the same way for the same job picker state.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return Number(getGlobalValue("getBuilderId"));
}

/**
 * Whether stored selections were made under the builder now in session.
 *
 * State written before the builder was recorded has no builderId and is trusted, so an existing
 * user's selection survives the deploy that adds the field.
 */
function belongsToCurrentBuilder(state: IJobPickerPersistantState): boolean {
    return state.builderId === undefined || state.builderId === getCurrentBuilderId();
}

/**
 * Reads the stored job picker state, dropping job selections that belong to a different builder.
 *
 * Storage is per-origin rather than per-session, so a BTAdmin who impersonates a second builder
 * would otherwise inherit the previous builder's selected job ids. Those ids resolve to jobs the
 * impersonated user cannot see, and the API rejects them as a missing entity ("This item may have
 * been deleted or your viewing permissions have changed"). Preferences that are not builder-specific
 * (template mode, filters) are kept either way.
 */
export function getJobPickerState(): IJobPickerPersistantState {
    const sessionState = BTSessionStorage.get("bt-object-dangerousJobPickerState");
    const localState = BTLocalStorage.get("bt-object-dangerousJobPickerState");

    // Session storage still wins when it is usable; only skip past it when it belongs to another
    // builder and local storage holds selections for the current one. Both reads are nullish-checked
    // because storage can be stubbed out or hold an explicit null in non-browser hosts.
    if (sessionState != null && belongsToCurrentBuilder(sessionState)) {
        return sessionState;
    }
    if (localState != null && belongsToCurrentBuilder(localState)) {
        return localState;
    }

    // Keep the non-builder-specific preferences off whichever state we are discarding, falling back
    // to the storage defaults when neither store held one.
    const staleState =
        sessionState ??
        localState ??
        localStorageDefaultValues["bt-object-dangerousJobPickerState"];
    return { ...staleState, selectedJobIds: [], isAllJobsSelected: false };
}

export function setJobPickerState(state: IJobPickerPersistantState) {
    const stateForBuilder = { ...state, builderId: getCurrentBuilderId() };
    BTSessionStorage.set("bt-object-dangerousJobPickerState", stateForBuilder);
    BTLocalStorage.set("bt-object-dangerousJobPickerState", stateForBuilder);
}

export function removeJobPickerState() {
    BTSessionStorage.remove("bt-object-dangerousJobPickerState");
    BTLocalStorage.remove("bt-object-dangerousJobPickerState");
}
